/**
 * Widget Initialization Check - Orchestrates widget initialization decisions
 *
 * This module combines two layers of checks:
 *
 * Layer 1: Traffic Control (gradual rollout)
 * - Purpose: Gradual widget rollout via traffic allocation
 * - Checked: Once at widget initialization
 * - Function: checkTrafficAllocation()
 *
 * Layer 2: Site Restrictions (URL, dataLayer, device, config)
 * - Purpose: URL patterns, dataLayer conditions, device compatibility, business rules
 * - Checked: At initialization and on URL changes
 * - Functions: isUrlAllowed(), isCurrentDeviceAllowed() from shared domain utilities
 *
 * Usage:
 * - canInitializeWidget(): Use at widget init - checks both layers
 * - shouldDisplayOnCurrentUrl(): Use during widget lifecycle - checks display rules only
 */

import type { SiteConfig } from '../../config/types';
import { logger } from '../core/logger';
import { isMobileDevice } from '../device/mobileDetection';
import { isDataLayerAllowed } from '../domain/dataLayerDisplayConditions';
import { isCurrentDeviceAllowed, isUrlAllowed } from '../domain/siteRestrictions';
import { checkTrafficAllocation, type TrafficControlResult } from './trafficControl';

/**
 * Detailed display decision information for analytics
 */
interface DisplayDecisionDetails {
    // Traffic Control (Layer 1)
    trafficControlPassed: boolean;
    trafficControlReason?: string;
    userBucket?: number;

    // Display Rules (Layer 2)
    urlAllowed: boolean;
    dataLayerAllowed: boolean;
    deviceAllowed: boolean;
    isMobile: boolean;
    mobileEnabled: boolean;

    // Configuration
    forceDisplay: boolean;
    currentUrl: string;
}

/**
 * Complete initialization check result with detailed decision data
 */
interface InitializationResult {
    allowed: boolean;
    reason?: string;
    details: DisplayDecisionDetails;
}

/**
 * LAYER 2: Display Rules Check (Sync, config-based)
 *
 * This function checks page-scoped display rules for the current page.
 * It does NOT check traffic control or device compatibility - those stay in canInitializeWidget().
 *
 * Use this function:
 * - During widget lifecycle when URL changes (navigation)
 * - When you need to re-check page eligibility without re-running bootstrap checks
 *
 * @param siteConfig - Site configuration from database
 * @param options - Display options
 * @param options.currentUrl - URL to check against patterns (defaults to window.location.href)
 * @returns true if the current page is allowed by display configuration
 */
export function shouldDisplayOnCurrentUrl(
    siteConfig: SiteConfig | null,
    options: {
        currentUrl?: string;
    } = {}
): boolean {
    const { currentUrl = typeof window !== 'undefined' ? window.location.href : '' } = options;

    logger.debug('🎯 shouldDisplayOnCurrentUrl: Evaluating display decision', {
        currentUrl,
        hasConfig: !!siteConfig,
        includePatterns: siteConfig?.display_only_on_urls,
    });

    const urlAllowed = isUrlAllowed(siteConfig, currentUrl);
    if (!urlAllowed) {
        logger.info(
            '❌ shouldDisplayOnCurrentUrl: Current URL blocked by exclusion/inclusion patterns'
        );
        return false;
    }

    const dataLayerAllowed = isDataLayerAllowed(
        siteConfig?.custom_config?.data_layer_display_conditions
    );
    if (!dataLayerAllowed) {
        logger.info('❌ shouldDisplayOnCurrentUrl: Current dataLayer blocked by display rules');
        return false;
    }

    logger.info('✅ shouldDisplayOnCurrentUrl: Current page allowed by display rules');
    return true;
}

/**
 * ORCHESTRATOR: Complete Widget Initialization Check
 *
 * This function orchestrates BOTH layers:
 * - Layer 1: Traffic Control (Supabase-based deterministic bucketing)
 * - Layer 2: Display Rules (URL/device configuration)
 *
 * Use this function ONCE at widget initialization.
 * For subsequent URL changes, use shouldDisplayOnCurrentUrl() instead.
 *
 * Decision flow:
 * 1. forceDisplay → skip traffic control and device check, still check display rules
 * 2. Traffic Control (Supabase) → if blocked, immediate NO
 * 3. Current page rules → if blocked, immediate NO
 * 4. Device compatibility → if blocked, immediate NO
 * 5. All passed → YES
 *
 * Note: PostHog instance is obtained internally via getPostHog() when needed
 * for stable clientId generation. Ensure PostHog is initialized before calling.
 *
 * @param siteConfig - Site configuration from database
 * @param options - Display options
 * @param options.forceDisplay - If true, skips traffic control and device check but still enforces page display rules
 * @param options.currentUrl - URL to check against patterns
 * @returns { allowed: boolean, reason?: string, details: DisplayDecisionDetails }
 */
export function canInitializeWidget(
    siteConfig: SiteConfig | null,
    options: {
        forceDisplay?: boolean;
        currentUrl?: string;
    } = {}
): InitializationResult {
    const {
        forceDisplay = false,
        currentUrl = typeof window !== 'undefined' ? window.location.href : '',
    } = options;

    logger.debug('🎯 canInitializeWidget: Starting complete initialization check', {
        domain: siteConfig?.domain,
        hasSiteConfig: !!siteConfig,
        forceDisplay,
    });

    // Initialize decision details (will be populated throughout the checks)
    const isMobile = isMobileDevice();
    const mobileEnabled = siteConfig?.custom_config?.enable_mobile === true;

    // Layer 0: Force display — skip traffic control but still check display rules
    if (forceDisplay) {
        logger.info('✅ canInitializeWidget: forceDisplay enabled - skipping traffic control');
        const urlAllowed = isUrlAllowed(siteConfig, currentUrl);
        const dataLayerAllowed = isDataLayerAllowed(
            siteConfig?.custom_config?.data_layer_display_conditions
        );

        if (!urlAllowed || !dataLayerAllowed) {
            logger.info('❌ canInitializeWidget: forceDisplay but display rules did not pass', {
                urlAllowed,
                dataLayerAllowed,
            });
            return {
                allowed: false,
                reason: !urlAllowed
                    ? 'URL not allowed by display rules (forceDisplay skips traffic control and device check only)'
                    : 'Data layer display conditions not met (forceDisplay skips traffic control and device check only)',
                details: {
                    trafficControlPassed: true,
                    userBucket: undefined,
                    urlAllowed,
                    dataLayerAllowed,
                    deviceAllowed: true,
                    isMobile,
                    mobileEnabled,
                    forceDisplay: true,
                    currentUrl,
                },
            };
        }

        return {
            allowed: true,
            reason: 'forceDisplay enabled, display rules passed',
            details: {
                trafficControlPassed: true,
                userBucket: undefined,
                urlAllowed: true,
                dataLayerAllowed,
                deviceAllowed: true,
                isMobile,
                mobileEnabled,
                forceDisplay: true,
                currentUrl,
            },
        };
    }

    // Layer 1: Traffic Control (Supabase-based deterministic bucketing)
    // Note: checkTrafficAllocation will get PostHog instance internally via getStableClientId()
    logger.debug('🚦 canInitializeWidget: Checking traffic control (Layer 1)...');
    const trafficResult: TrafficControlResult = checkTrafficAllocation(siteConfig);

    if (!trafficResult.enabled) {
        logger.info(`❌ canInitializeWidget: Traffic control blocked - ${trafficResult.reason}`);

        // Still check display rules for analytics (even though traffic control failed)
        const urlAllowed = isUrlAllowed(siteConfig, currentUrl);
        const dataLayerAllowed = isDataLayerAllowed(
            siteConfig?.custom_config?.data_layer_display_conditions
        );
        const deviceAllowed = isCurrentDeviceAllowed(siteConfig);

        return {
            allowed: false,
            reason: `Traffic control: ${trafficResult.reason}`,
            details: {
                trafficControlPassed: false,
                trafficControlReason: trafficResult.reason,
                userBucket: trafficResult.bucket,
                urlAllowed,
                dataLayerAllowed,
                deviceAllowed,
                isMobile,
                mobileEnabled,
                forceDisplay: false,
                currentUrl,
            },
        };
    }

    logger.debug('✅ canInitializeWidget: Traffic control passed');

    // Layer 2: Display Rules (current page only)
    logger.debug('📋 canInitializeWidget: Checking current page rules (Layer 2)...');
    const urlAllowed = isUrlAllowed(siteConfig, currentUrl);
    if (!urlAllowed) {
        logger.info('❌ canInitializeWidget: Current page blocked by display rules');
        // Still run device check so bootstrapAllowed reflects actual device eligibility.
        // Without this, SPA navigation to a whitelisted URL would bypass the mobile check.
        const deviceAllowedEarly = isCurrentDeviceAllowed(siteConfig);
        return {
            allowed: false,
            reason: 'URL not allowed by site configuration',
            details: {
                trafficControlPassed: true,
                trafficControlReason: undefined,
                userBucket: trafficResult.bucket,
                urlAllowed,
                dataLayerAllowed: true,
                deviceAllowed: deviceAllowedEarly,
                isMobile,
                mobileEnabled,
                forceDisplay: false,
                currentUrl,
            },
        };
    }

    logger.debug('✅ canInitializeWidget: Current page rules passed');

    const dataLayerAllowed = isDataLayerAllowed(
        siteConfig?.custom_config?.data_layer_display_conditions
    );
    if (!dataLayerAllowed) {
        logger.info('❌ canInitializeWidget: Current dataLayer blocked by display rules');
        const deviceAllowedEarly = isCurrentDeviceAllowed(siteConfig);
        return {
            allowed: false,
            reason: 'Data layer display conditions not met',
            details: {
                trafficControlPassed: true,
                trafficControlReason: undefined,
                userBucket: trafficResult.bucket,
                urlAllowed,
                dataLayerAllowed,
                deviceAllowed: deviceAllowedEarly,
                isMobile,
                mobileEnabled,
                forceDisplay: false,
                currentUrl,
            },
        };
    }

    logger.debug('✅ canInitializeWidget: Data layer display rules passed');

    // Layer 3: Device compatibility
    logger.debug('📱 canInitializeWidget: Checking device compatibility (Layer 3)...');
    const deviceAllowed = isCurrentDeviceAllowed(siteConfig);
    if (!deviceAllowed) {
        logger.info('❌ canInitializeWidget: Device type blocked widget');
        return {
            allowed: false,
            reason: 'Device type not allowed by site configuration',
            details: {
                trafficControlPassed: true,
                trafficControlReason: undefined,
                userBucket: trafficResult.bucket,
                urlAllowed: true,
                dataLayerAllowed,
                deviceAllowed,
                isMobile,
                mobileEnabled,
                forceDisplay: false,
                currentUrl,
            },
        };
    }

    logger.debug('✅ canInitializeWidget: Device compatibility passed');

    // All checks passed
    logger.info('✅ canInitializeWidget: All initialization checks passed');
    return {
        allowed: true,
        details: {
            trafficControlPassed: true,
            trafficControlReason: undefined,
            userBucket: trafficResult.bucket,
            urlAllowed,
            dataLayerAllowed,
            deviceAllowed,
            isMobile,
            mobileEnabled,
            forceDisplay: false,
            currentUrl,
        },
    };
}
